import { LEGACY_SHARED_APPS, PARTNER_APPS } from "./constants";
/**
 * Set of known raw provider ID prefixes from authentication providers.
 * These appear at the start of raw provider IDs and should NOT be treated as namespaces.
 */
const KNOWN_RAW_PROVIDER_PREFIXES = new Set([
    "kp", // Kinde provider prefix
]);
/**
 * Returns true when a user ID has been namespaced (application or token-derived).
 *
 * Detection logic:
 * 1. If no colon → not namespaced (e.g., "uuid")
 * 2. If first part is a known raw provider prefix → not namespaced (e.g., "kp:123")
 * 3. Otherwise → namespaced (e.g., "secretmate:kp:123", "secretmate-brand:kp:123")
 *
 * Examples:
 * - `secretmate:kp:123` → true (secretmate is NOT a raw provider prefix)
 * - `secretmate-brand:kp:123` → true (token-derived namespace for SECRETMATE)
 * - `external_org:kp:123` → true (token-derived namespace)
 * - `kp:123` → false (kp IS a raw provider prefix)
 * - `kp:123:extra` → false (kp IS a raw provider prefix)
 * - `uuid` → false (no colon)
 *
 * This properly handles:
 * - Raw IDs with 0 colons (uuid)
 * - Raw IDs with 1+ colons (kp:123, kp:123:extra)
 * - Kinde application namespaces (secretmate, spicychat, pixelchat)
 * - Token-derived namespaces (secretmate-brand, external_org, custom-tenant)
 */
const isNamespacedUserId = (userId) => {
    const firstColonIndex = userId.indexOf(":");
    if (firstColonIndex === -1) {
        return false;
    }
    const firstPart = userId.substring(0, firstColonIndex);
    // If the first part is a known raw provider prefix, it's NOT namespaced
    return !KNOWN_RAW_PROVIDER_PREFIXES.has(firstPart);
};
/**
 * Utility helpers for converting between raw provider user IDs and
 * application-scoped frontend user IDs.
 *
 * Rules:
 * - Legacy shared apps keep raw IDs unchanged
 * - Partner apps keep raw IDs unchanged (they manage their own identity)
 * - Kinde-based apps namespace IDs using `external_org_id` when available
 * - If no token namespace is provided, the application ID is used as fallback
 */
export const userIdUtils = {
    /**
     * Converts a raw provider user ID into the frontend application-scoped format.
     */
    toApplicationUserId({ rawUserId, applicationId, namespace }) {
        // Legacy shared apps and partner apps don't need namespacing
        if (LEGACY_SHARED_APPS.has(applicationId) || PARTNER_APPS.has(applicationId)) {
            return rawUserId;
        }
        if (isNamespacedUserId(rawUserId)) {
            return rawUserId;
        }
        const effectiveNamespace = namespace && namespace.length > 0 ? namespace : applicationId;
        return `${effectiveNamespace}:${rawUserId}`;
    },
    /**
     * Removes the leading namespace from an application-scoped user ID.
     */
    toRawUserId(userId) {
        if (!isNamespacedUserId(userId)) {
            return userId;
        }
        const firstColonIndex = userId.indexOf(":");
        if (firstColonIndex <= 0) {
            return userId;
        }
        return userId.substring(firstColonIndex + 1);
    },
    /**
     * Extracts the namespace portion from an application-scoped user ID.
     */
    getNamespaceFromUserId(userId) {
        if (!isNamespacedUserId(userId)) {
            return undefined;
        }
        const firstColonIndex = userId.indexOf(":");
        if (firstColonIndex <= 0) {
            return undefined;
        }
        return userId.substring(0, firstColonIndex);
    },
    /**
     * Public wrapper for namespace detection.
     */
    isNamespacedUserId(userId) {
        return isNamespacedUserId(userId);
    },
    /**
     * Compares two user IDs for equality, respecting namespace boundaries.
     * In multi-tenant contexts, users from different tenants with the same
     * raw provider ID are treated as distinct users.
     */
    isSameUser(firstUserId, secondUserId) {
        return firstUserId === secondUserId;
    },
};
